import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Pill from '@pelckmans/elementis/components/pill';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import CloseIcon from '@pelckmans/elementis/icons/close';
import PauseIcon from '@pelckmans/elementis/icons/pause';
import TasksIcon from '@pelckmans/elementis/icons/tasks';
import UserIcon from '@pelckmans/elementis/icons/user';
import { ComponentProps, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MedialinkDialogHeaderTagsWithoutPillGroup } from '../../../../../../pages/material/components/MedialinkDialogHeaderTags';
import { assignmentsEndIn24hours, assignmentsEndIn7Days } from '../../../../../../pages/material/utils/assignmentUtils';
import { formatAssignmentToDate } from '../../../../../../utils/formatDate';
import { TOKEN_ACCESSOR } from '../../../../../../utils/tokenAccessor';
import { ANALYTICS_EVENT_TYPES, AnalyticsData } from '../../../../hooks/constants/Analytics';
import useAnalytics from '../../../../hooks/useAnalytics';
import { ExercisePhase } from '../../@types/exercise-types';
import * as exerciseApi from '../../api/exercises-api';
import { useExerciseManagerContext } from '../../contexts/ExerciseManagerContext';
import { ExerciseCalculating } from '../exercise-calculating';
import { ExerciseIframe } from '../exercise-iframe';
import { ExerciseLoading } from '../exercise-loading';
import RetryAlert from '../retry-alert';
import ServiceNotAvailable from '../service-not-available';

interface ActiveSessionModalProps {
  phaseAction: ExercisePhase | undefined;
  analyticsData?: AnalyticsData;
  setPhase: Dispatch<SetStateAction<'launch' | 'activity' | 'progression' | undefined>>;
  onSessionPaused: (session: exerciseApi.PauseExerciseSessionResponse) => void;
  onSessionFinished: (result: exerciseApi.StopExerciseSessionResponse) => void;
}

function ActiveSessionModal({
  phaseAction,
  analyticsData,
  setPhase,
  onSessionPaused,
  onSessionFinished,
}: ActiveSessionModalProps) {
  const { assignmentId, module, language, studioGroup, medialink, onClose } = useExerciseManagerContext();
  const analytics = useAnalytics();
  const { t } = useTranslation();

  const [url, setUrl] = useState<string>();
  const [state, setState] = useState<'loading' | 'play' | 'calculating-paused-score' | 'calculating-final-score'>(
    'loading',
  );
  const [serviceAvailable, setServiceAvailable] = useState<boolean>(true);
  const [retryAction, setRetryAction] = useState<() => Promise<void>>();

  const eventCaptured = useRef<boolean>(false);

  useEffect(() => {
    const play = async (): Promise<void> => {
      const token = await TOKEN_ACCESSOR.tokenGetter();

      let exerciseUrl;

      if (assignmentId) {
        if (phaseAction === 'start') {
          exerciseUrl = await exerciseApi.startAssignmentSession(
            token!,
            module.id,
            assignmentId,
            language,
            studioGroup,
          );
        }
        if (phaseAction === 'resume') {
          exerciseUrl = await exerciseApi.resumeAssignmentSession(token!, module.id, assignmentId, studioGroup);
        }
      } else {
        if (phaseAction === 'start') {
          exerciseUrl = await exerciseApi.startFreeExerciseSession(
            token!,
            module.id,
            medialink.id,
            language,
            studioGroup,
          );
        }
        if (phaseAction === 'resume') {
          exerciseUrl = await exerciseApi.resumeFreeExerciseSession(token!, module.id, medialink.id, studioGroup);
        }
      }

      if (exerciseUrl) setUrl(exerciseUrl);

      setState('play');
    };

    const run = async () => {
      if (state === 'loading') {
        try {
          await play();
        } catch (e) {
          setServiceAvailable(false);
        }
      }
    };

    run();
  }, [assignmentId, state, language, module.id, studioGroup, phaseAction, medialink.id]);

  useEffect(() => {
    const captureEvent = async (): Promise<void> => {
      const token = await TOKEN_ACCESSOR.tokenGetter();

      if (analyticsData && token && !eventCaptured.current) {
        eventCaptured.current = true;
        analytics.capture(
          {
            eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
            objectId: medialink.id,
            analyticsData,
          },
          token,
        );
      }
    };

    captureEvent();
  }, [analytics, analyticsData, medialink.id]);

  const handlePause = async (): Promise<void> => {
    setState('calculating-paused-score');
    setRetryAction(undefined);

    try {
      const token = await TOKEN_ACCESSOR.tokenGetter();

      const data = assignmentId
        ? await exerciseApi.pauseAssignmentSession(token!, module.id, assignmentId, studioGroup)
        : await exerciseApi.pauseFreeExerciseSession(token!, module.id, medialink.id, studioGroup);

      onSessionPaused(data);

      setPhase('progression');
    } catch (e) {
      setRetryAction(() => handlePause);
    }
  };

  const handleFinish = useCallback(async () => {
    setRetryAction(undefined);
    setState('calculating-final-score');

    try {
      const token = await TOKEN_ACCESSOR.tokenGetter();
      const data = assignmentId
        ? await exerciseApi.stopAssignmentSession(token!, module.id, assignmentId, studioGroup)
        : await exerciseApi.stopFreeExerciseSession(token!, module.id, medialink.id, studioGroup);

      onSessionFinished(data);

      setPhase('progression');
    } catch (e) {
      setRetryAction(() => handleFinish);
    }
  }, [assignmentId, module.id, studioGroup, medialink.id, setPhase, onSessionFinished]);

  const dialogHeaderProps = () => {
    if (!serviceAvailable || retryAction) {
      return {
        buttonGroupProps: {
          'aria-label': undefined,
          role: undefined,
          children: (
            <Button prominence="neutral" startIcon={<CloseIcon data-testid="close-icon" />} onClick={onClose}>
              {t('EXERCISE_MANAGER.CLOSE_DIALOG')}
            </Button>
          ),
        },
      };
    }

    switch (state) {
      case 'loading':
      case 'calculating-final-score':
        return {
          buttonGroupProps: {
            'aria-label': undefined,
            role: undefined,
            children: (
              <Button
                prominence="neutral"
                startIcon={<CloseIcon data-testid="close-icon" />}
                onClick={onClose}
                busy={state === 'calculating-final-score'}
              >
                {t('EXERCISE_MANAGER.CLOSE_DIALOG')}
              </Button>
            ),
          },
        };
      case 'play':
      case 'calculating-paused-score':
        return {
          buttonGroupProps: {
            'aria-label': undefined,
            role: undefined,
            children: (
              <Button
                prominence="neutral"
                startIcon={<PauseIcon data-testid="pause-icon" />}
                onClick={handlePause}
                busy={state === 'calculating-paused-score'}
              >
                {t('EXERCISE_MANAGER.ACTIVE_SESSION.BUTTONS.PAUSE')}
              </Button>
            ),
          },
        };
    }
  };

  const medialinkAssignment = medialink.assignments.find(assignment => 'current' in assignment);

  const pillProminence: ComponentProps<typeof Pill>['prominence'] = useMemo(() => {
    if (!medialinkAssignment) return undefined;

    if (assignmentsEndIn24hours(medialinkAssignment)) {
      return 'danger';
    } else if (assignmentsEndIn7Days(medialinkAssignment)) {
      return 'warning';
    }

    return 'neutral';
  }, [medialinkAssignment]);

  return (
    <Dialog
      id="active-session-dialog"
      modal
      aria-label={t('EXERCISE_MANAGER.ACTIVE_SESSION.A11Y_LABEL')}
      onClose={onClose}
      size="full"
    >
      <Dialog.Header title={medialink.name} {...dialogHeaderProps()} titleTooltip={medialink.name}>
        <PillGroup aria-label={undefined} role={undefined}>
          <MedialinkDialogHeaderTagsWithoutPillGroup material={medialink} module={module} />
          {medialinkAssignment && (
            <>
              <Pill key="assignment-pill" icon={<TasksIcon />}>
                {t('EXERCISE_MANAGER.ACTIVE_SESSION.PILLS.ASSIGNMENT')}
              </Pill>
              <Pill key="deadline-pill" prominence={pillProminence}>
                {t('EXERCISE_MANAGER.ACTIVE_SESSION.PILLS.TO', { to: formatAssignmentToDate(medialinkAssignment.to) })}
              </Pill>
              {medialinkAssignment.user && (
                <Pill
                  key="user-pill"
                  icon={<UserIcon />}
                >{`${medialinkAssignment.user.firstName} ${medialinkAssignment.user.lastName}`}</Pill>
              )}
            </>
          )}
        </PillGroup>
      </Dialog.Header>
      <Dialog.Body>
        {!serviceAvailable && <ServiceNotAvailable />}

        {serviceAvailable && state === 'loading' && (
          <ExerciseLoading
            loadingText={{
              title: t('EXERCISE_MANAGER.EXERCISE_LOADING.TITLE'),
              p1: t('EXERCISE_MANAGER.EXERCISE_LOADING.P1'),
              p2: t('EXERCISE_MANAGER.EXERCISE_LOADING.P2'),
            }}
          />
        )}

        {serviceAvailable && url && (state === 'play' || state === 'calculating-paused-score') && (
          <>
            {retryAction && (
              <RetryAlert
                onRetry={retryAction}
                message={t('EXERCISE_MANAGER.ACTIVE_SESSION.ALERTS.RETRY_ACTION.MESSAGE')}
                ctaText={t('EXERCISE_MANAGER.ACTIVE_SESSION.ALERTS.RETRY_ACTION.BUTTONS.RETRY')}
              />
            )}
            <ExerciseIframe exerciseUrl={url} medialink={medialink} onExerciseFinished={handleFinish} />
          </>
        )}

        {serviceAvailable && state === 'calculating-final-score' && (
          <>
            {retryAction && (
              <RetryAlert
                onRetry={retryAction}
                message={t('EXERCISE_MANAGER.ACTIVE_SESSION.ALERTS.RETRY_ACTION.MESSAGE')}
                ctaText={t('EXERCISE_MANAGER.ACTIVE_SESSION.ALERTS.RETRY_ACTION.BUTTONS.RETRY')}
              />
            )}
            <ExerciseCalculating />
          </>
        )}
      </Dialog.Body>
    </Dialog>
  );
}

export default ActiveSessionModal;
